<script lang="ts" setup>
  import type { PropType } from 'vue'
  import { EntitiesMapperKeyEnum, type TDataMapper } from '@/types/generated'
  import { FieldTypeEnum } from '@/types/generated'
  import { computed, ref, watch } from 'vue'
  import { yup } from '@/lib/validation'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import { CheckIcon, CopyIcon, ClockIcon } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'

  const props = defineProps({
    value: {
      // Using type: null tells Vue to accept any type for this prop
      // This is the Vue-specific way to allow any value type
      type: null,
      required: true
    },
    mapper: {
      type: Object as PropType<TDataMapper>,
      required: true
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    }
  })

  const emit = defineEmits(['onValueChange'])

  const localValue = ref(props.value)

  // Update local value when prop changes
  watch(
    () => props.value,
    (newValue) => {
      localValue.value = newValue
    }
  )

  const validationRules = computed(() => {
    if (!props.mapper.field_type) return null

    const schema = (() => {
      switch (props.mapper.field_type) {
        case FieldTypeEnum.NUMBERINPUT:
          return yup.number().integer()
        case FieldTypeEnum.DECIMALNUMBERINPUT:
          return yup.number()
        case FieldTypeEnum.TEXTINPUT || FieldTypeEnum.TEXTAREA:
          return yup.string()
        case FieldTypeEnum.EMAILINPUT:
          return yup.string().email()
        case FieldTypeEnum.PHONEINPUT:
          return yup.string().phone()
        case FieldTypeEnum.URLINPUT:
          return yup.string().url()
        case FieldTypeEnum.SELECT:
          return yup.string().oneOf(props.mapper.choices || [], 'Please select a valid option')
        case FieldTypeEnum.DATEFIELD:
          return yup
            .string()
            .matches(
              /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.(19|20)\d{2}$/,
              'Bitte geben Sie ein gültiges Datum im Format TT.MM.JJJJ ein'
            )
        case FieldTypeEnum.TIMEFIELD:
          return yup
            .string()
            .matches(
              /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/,
              'Bitte geben Sie eine gültige Uhrzeit im Format HH:MM ein'
            )
        default:
          return null
      }
    })()

    return schema
  })

  const modelValue = computed({
    get: () => localValue.value,
    set: async (newValue) => {
      const normalizedNewValue = newValue

      if (localValue.value === normalizedNewValue) {
        return
      }

      if (!validationRules.value) {
        localValue.value = normalizedNewValue
        emit('onValueChange', normalizedNewValue)
        return
      }

      try {
        await validationRules.value.validate(normalizedNewValue)
        localValue.value = normalizedNewValue
        emit('onValueChange', normalizedNewValue)
      } catch (error) {
        // Value is invalid, don't emit the change
        console.log('Validation failed:', error)
      }
    }
  })

  const renderInputField = computed<Boolean>(() => {
    // if the mapper field type is in textinput, numberinput, emailinput etc
    return [
      FieldTypeEnum.TEXTINPUT,
      FieldTypeEnum.NUMBERINPUT,
      FieldTypeEnum.DECIMALNUMBERINPUT,
      FieldTypeEnum.EMAILINPUT,
      FieldTypeEnum.PHONEINPUT,
      FieldTypeEnum.URLINPUT,
      FieldTypeEnum.DATEFIELD,
      FieldTypeEnum.TIMEFIELD
    ].includes(props.mapper.field_type)
  })

  const renderCheckboxField = computed<Boolean>(() => {
    return props.mapper.field_type == FieldTypeEnum.YESNO
  })

  const renderSelectField = computed<Boolean>(() => {
    return props.mapper.field_type == FieldTypeEnum.SELECT
  })

  const renderTextareaField = computed<Boolean>(() => {
    return props.mapper.field_type == FieldTypeEnum.TEXTAREA
  })

  const selectFieldOptions = computed(() => {
    // hardcoding the title of entity_types temporarily
    // TODO: find a solution for that later
    if (props.mapper.key === EntitiesMapperKeyEnum.ENTITIES_FIELD_ENTITY_TYPE) {
      return [
        {
          title: 'Natürliche Person',
          value: 'natural_person'
        },
        {
          title: 'Juristiche Person',
          value: 'legal_person'
        },
        {
          title: 'Institution',
          value: 'institution'
        }
      ]
    }
    return props.mapper.choices?.map((option) => {
      return {
        title: option === '' ? '---' : option,
        value: option
      }
    })
  })

  const isCopied = ref(false)
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(String(localValue.value || ''))
      isCopied.value = true
      setTimeout(() => {
        isCopied.value = false
      }, 2000)
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }
</script>

<template>
  <div
    v-if="mapper"
    class="flex w-full items-center"
  >
    <div class="w-full">
      <InputField
        v-if="renderInputField"
        v-model="modelValue"
        :type="
          mapper.field_type == FieldTypeEnum.NUMBERINPUT ||
          mapper.field_type == FieldTypeEnum.DECIMALNUMBERINPUT
            ? 'number'
            : mapper.field_type == FieldTypeEnum.TIMEFIELD
              ? 'time'
              : 'text'
        "
        :name="mapper.key"
        :rules="validationRules"
        :step="mapper.field_type == FieldTypeEnum.DECIMALNUMBERINPUT ? '0.01' : '1'"
        :disabled="disabled"
      />
      <SelectField
        v-else-if="renderSelectField"
        v-model="modelValue"
        :options="selectFieldOptions"
        :name="mapper.key"
        :rules="validationRules"
        :disabled="disabled"
      />
      <CheckboxField
        v-else-if="renderCheckboxField"
        v-model="modelValue"
        :name="mapper.key"
        :disabled="disabled"
      />
      <TextAreaField
        v-else-if="renderTextareaField"
        v-model="modelValue"
        :name="mapper.key"
        :rules="validationRules"
        :disabled="disabled"
      />

      <div v-else>
        field: {{ mapper.field_type }}
        <br />
        rules: {{ validationRules }}
        <span class="strong">NOT YET IMPLEMENTED</span>
      </div>
    </div>
    <div class="flex items-center">
      <Button
        variant="ghost"
        size="icon"
        @click="copyToClipboard"
      >
        <CopyIcon
          v-if="!isCopied"
          class="h-4 w-4"
        />
        <span
          v-else
          class="text-xs"
        >
          <CheckIcon class="h-4 w-4 text-green-500" />
        </span>
      </Button>
    </div>
    <!--    <pre>-->
    <!--      {{ mapper }}-->
    <!--    </pre>-->
  </div>
</template>
